import { ReadonlyMat4, ReadonlyVec3, vec3 } from 'gl-matrix';
import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { isRigidRegistration } from '~/domain/patient/rigidRegistration';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useEnvironmentShowing } from '~/hooks/environment/useEnvironmentShowing';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { referenceComparisonState } from '~/state/atoms/viewportGridAtoms';
import { mirrorViewPositions, PointMapping, tieCrosshairs } from './rowTie';

const identity: PointMapping = (pointMm) => vec3.clone(pointMm);

const through =
    (transform: ReadonlyMat4 | undefined): PointMapping =>
    (pointMm: ReadonlyVec3) =>
        transform
            ? vec3.transformMat4(vec3.create(), pointMm, transform)
            : null;

function samePlaneViews(
    from: ReadonlyArray<PlaneView>,
    to: ReadonlyArray<PlaneView>
): Array<[PlaneView, PlaneView]> {
    return from.flatMap((view): Array<[PlaneView, PlaneView]> => {
        const match = to.find((other) => other.label === view.label);
        return match ? [[view, match]] : [];
    });
}

// Ties the reference row to the primary row through the rigid registration: crosshair both ways, zoom and pan from the primary.
export function useReferenceRowTie(): void {
    const comparison = useRecoilValue(referenceComparisonState);
    const patient = useSelectedPatient();
    const viewManager = useRecoilValue(viewManagerState);
    const primaryImage = useSelectedPrimaryImage();
    const referenceImage = patient?.images.find(
        (image) => image.seriesUid === comparison?.seriesUid
    );
    const primary = useEnvironmentShowing(primaryImage);
    const reference = useEnvironmentShowing(referenceImage);
    const registration = comparison?.registrationSopUid
        ? patient?.getRegistrationWithSopInstanceUid(
              comparison.registrationSopUid
          )
        : undefined;

    useEffect(() => {
        if (
            !viewManager ||
            !primary ||
            !reference ||
            !primaryImage ||
            !referenceImage
        ) {
            return;
        }
        const primaryFrame = primaryImage.frameOfReferenceUid;
        const referenceFrame = referenceImage.frameOfReferenceUid;
        const rigid =
            registration && isRigidRegistration(registration)
                ? registration
                : undefined;
        let into = identity;
        let back = identity;
        if (primaryFrame !== referenceFrame) {
            // Two frames with no registration between them are not tied.
            if (!rigid) {
                return;
            }
            into = through(
                rigid.getTransformFromTo(primaryFrame, referenceFrame)
            );
            back = through(
                rigid.getTransformFromTo(referenceFrame, primaryFrame)
            );
        }

        let following = false;
        const followZoomAndPan = (): void => {
            if (following) {
                return;
            }
            following = true;
            try {
                reference.panZoom.scale = primary.panZoom.scale;
                mirrorViewPositions(
                    samePlaneViews(
                        viewManager.viewsOf(primary),
                        viewManager.viewsOf(reference)
                    ),
                    into
                );
            } finally {
                following = false;
            }
        };

        const start = into(primary.planeIntersectionPoint.coordinates);
        if (start) {
            reference.planeIntersectionPoint.coordinates = start;
        }
        followZoomAndPan();
        const untieCrosshairs = tieCrosshairs(primary, reference, into, back);
        viewManager.addEventListener('zoomScaleChanged', followZoomAndPan);
        viewManager.addEventListener('viewPositionChanged', followZoomAndPan);
        return () => {
            untieCrosshairs();
            viewManager.removeEventListener(
                'zoomScaleChanged',
                followZoomAndPan
            );
            viewManager.removeEventListener(
                'viewPositionChanged',
                followZoomAndPan
            );
        };
    }, [
        viewManager,
        primary,
        reference,
        primaryImage,
        referenceImage,
        registration,
    ]);
}
